import { observer } from 'mobx-react';
import React, { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Descendant, createEditor } from 'slate';
import { Editable, RenderElementProps, RenderLeafProps, Slate, withReact } from 'slate-react';

import * as faIcons from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames/dedupe';
import { withHistory } from 'slate-history';
import uniqid from 'uniqid';
import { useFieldSetContext } from '../forms';
import { MacroDropdown } from './MacroDropdown';
import { RichTextBoxSerialization } from './RichTextBoxSerialization';
import {
    RichTextBoxFormatValue,
    RichTextBoxProps
} from './richTextEditorTypes';
import { RichTextBoxToolbar } from './toolbar';
import { withRichTextBox } from './withRichTextBox';

/**
 * This tells slate how to render elements in our rich text editor.  We have complete control
 * over how it renders them.
**/
const InternalElement = observer(({ attributes, children, element, wrapperRef }: RenderElementProps & { wrapperRef: React.RefObject<HTMLDivElement> }) => {
    const style: React.CSSProperties = {};

    if (element.indentationLevel && element.indentationLevel > 0) {
        style.marginLeft = element.indentationLevel.toString() + 'rem';
    }
    else
        style.marginLeft = undefined;

    switch (element.type) {
        case 'bulleted-list':
            {
                return (
                    <ul style={style} {...attributes}>
                        {children}
                    </ul>
                )
            }
        case 'numbered-list': {
            return (
                <ol style={style} {...attributes}>
                    {children}
                </ol>
            )
        }
        case "list-item": {
            return (
                <li style={style} {...attributes}>{children}</li>
            )
        }
        case 'paragraph':
        default:
            {
                return (
                    <p style={style} {...attributes}>
                        {children}
                    </p>
                )
            }
    }
});

const InternalLeaf = observer(({ leaf, children, attributes, wrapperRef }: RenderLeafProps & { wrapperRef: React.RefObject<HTMLDivElement> }) => {
    const leafRef = useRef<HTMLSpanElement | null>(null);
    const [domElement, setDomElement] = useState<HTMLSpanElement | null>(null);

    useEffect(() => {
        if (leafRef.current)
            setDomElement(leafRef.current);
        else
            setDomElement(null);
    }, [leafRef]);

    const style: CSSProperties = {
    }

    if (leaf.color) {
        style.color = leaf.color;
    }

    const format = leaf.format ?? RichTextBoxFormatValue.none;
    let element = children;

    //check if format bits are set for bold/italic/underline, number & another number tells you if the bits in number are in another number
    if ((format & RichTextBoxFormatValue.bold) === RichTextBoxFormatValue.bold) {
        element = <strong>{element}</strong>;
    }

    if ((format & RichTextBoxFormatValue.italic) === RichTextBoxFormatValue.italic) {
        element = <i>{element}</i>;
    }

    if ((format & RichTextBoxFormatValue.underline) === RichTextBoxFormatValue.underline) {
        element = <u>{element}</u>;
    }

    return (
        <>
            <span ref={leafRef} style={style} className={classNames({ 'macroSearch': leaf.isMacroSearch })} {...attributes}>
                {element}
            </span>
            {
                domElement && leaf.isMacroSearch && <MacroDropdown wrapperRef={wrapperRef} element={domElement} searchText={leaf.text} />
            }
        </>
    )
});


export const RichTextBox = observer(({
    placeholder, rows, value, className, disabled, onChange, onBlur,
	inputRef,
    editableProps: {
        disabled: editorDisabled = false,
        spellCheck = true,
        className: editableClassName,
        readOnly,
        ...remainingEditableProps
    } = { readOnly: false },
    toolbarProps: {
        className: toolbarClassName,
        ...toolbarAttrs
    } = { className: undefined },
    ...remainingSlateProps }: RichTextBoxProps) => {

    const { isInFieldSet, fieldSetActions } = useFieldSetContext();
    const onChangeBounceRef = React.useRef<((descendant: Descendant[]) => void) | undefined>(undefined);
    const [initialHtml, setInitialHtml] = React.useState<string>("")
    const [initialDocument, setInitialDocument] = React.useState<Descendant[]>([{ type: 'paragraph', children: [{ text: '' }] }]);
    const [internalDocument, setInternalDocument] = React.useState<Descendant[] | null>(null);
	let wrapperRef = useRef<HTMLDivElement>(null);
	// Override the default Ref if the RHF ref is passed in the parent component.
    if (inputRef !== undefined)
		wrapperRef = inputRef as React.RefObject<HTMLDivElement>; // casting as the type used throughout the component
    //---------------

    useEffect(() => {
        if ((value ?? "") === initialHtml) //initialHtml is updated by the onChange handler below to prevent round robin updates from RHF
            return;
        setInitialHtml(value ?? "");
        const schema = RichTextBoxSerialization.parseHtmlToRichText(value);
        if (!_.isEqual(schema, internalDocument)) //only update the internalDocument if it has actually changed coming in from RHF
            setInitialDocument(schema);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);


    //generate the editor and a key so slate will recreate it's editor when the key changes
    //but only when the currentSchema has been updated externally via new value values.
    const [editor, editorKey] = useMemo(() => {
        const key = uniqid();
        const newEditor = withRichTextBox(withReact(withHistory(createEditor())), key) //<- this is where you wrap plugins around slate, withRichTextBoxConfig is our plugin
        return [
            newEditor,
            key
        ]
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialDocument]); //this dep is needed, it makes the memo recreate the editor anytime the initialDocument changes, and only if it changes, resetting the editor looses history

    //determines if the editor should be readonly multiple ways.
    const isReadOnly = useMemo(() => {
        if (isInFieldSet && fieldSetActions && fieldSetActions.disabled)
            return true;

        if (disabled || readOnly || editorDisabled)
            return true;

        return false;
    }, [isInFieldSet, fieldSetActions, disabled, readOnly, editorDisabled])

    //debounced onChangeHandler, so we don't serialize too fast
    const bouncedOnChange = useCallback((descendant: Descendant[]) => {
        const serializedRichText = RichTextBoxSerialization.serializeRichTextValueToString(descendant, true);

        setInitialHtml(serializedRichText.value ?? ""); //<- we set this here so when RHF comes back around it won't trigger an update again
        if (onChange)
            onChange(serializedRichText.value ?? '');
    }, [onChange]);

    /**
     * Wrapper around slates onChange handler that will debounce
     * it so we don't fire external components like React Hook Forms too fast.
     */
    const internalOnChange = useCallback((descendant: Descendant[]) => {
        if (isReadOnly)
            return;
        //don't process changes for selection changes, only for operational changes
        const isAstChange = editor.operations.some(
            op => 'set_selection' !== op.type
        )
        if (!isAstChange)
            return;

        setInternalDocument(descendant);

        if (!onChange)
            return;
        if (!onChangeBounceRef.current) {
            onChangeBounceRef.current = _.debounce((currentDescendant) => {
                bouncedOnChange(currentDescendant);
                onChangeBounceRef.current = undefined;
            }, 250, {
                leading: false,
                trailing: true
            });
        }
        if (onChangeBounceRef.current)
            onChangeBounceRef.current(descendant);
    }, [bouncedOnChange, isReadOnly, onChange, editor.operations]);

    useEffect(() => {
        //editablecontent divs don't support the rows attribute, so to be consistent with
        //everywhere we have rich text editors this **hack** will set minHeight
        const editorEl = document.querySelector<HTMLDivElement>(`#${editorKey}`);
        if (editorEl && rows)
            editorEl.style.minHeight = `${Math.max(rows * 1.5, 1.5)}rem`;
    }, [rows, editorKey]);

    return <Slate editor={editor} {...remainingSlateProps} value={initialDocument} key={editorKey} onChange={internalOnChange}>
        <div ref={wrapperRef} className={classNames("richtextbox", className)}>
            {!isReadOnly &&
                <RichTextBoxToolbar className={classNames(toolbarClassName, "")} {...toolbarAttrs}>
                    < div className="btn-group" >
                        <RichTextBoxToolbar.BlockButton elementType="numbered-list" attrs={{ title: "Number List" }} icon={<FontAwesomeIcon icon={faIcons.faListNumeric} />} />
                        <RichTextBoxToolbar.BlockButton elementType="bulleted-list" attrs={{ title: "Bullet List" }} icon={<FontAwesomeIcon icon={faIcons.faListDots} />} />
                    </div>
                    <div className="btn-group ps-2" role="group">
                        <RichTextBoxToolbar.FormatButton format={RichTextBoxFormatValue.bold} icon={<FontAwesomeIcon icon={faIcons.faBold} />} attrs={{ title: 'Bold: ctrl+b\nclear: ctrl+\\' }} />
                        <RichTextBoxToolbar.FormatButton format={RichTextBoxFormatValue.italic} icon={<FontAwesomeIcon icon={faIcons.faItalic} />} attrs={{ title: 'Italicize: ctrl+i\nclear: ctrl+\\' }} />
                        <RichTextBoxToolbar.FormatButton format={RichTextBoxFormatValue.underline} icon={<FontAwesomeIcon icon={faIcons.faUnderline} />} attrs={{ title: 'Underline: ctrl+u\nclear: ctrl+\\' }} />
                    </div>
                    <div className="btn-group ps-2">
                        <RichTextBoxToolbar.ColorPickerButton title="Font Color" />
                    </div>
                    <div className="btn-group ps-2">
                        <RichTextBoxToolbar.IndentButton attrs={{ title: "Decrease Indent: ctrl+shift+space\nclear: ctrl+\\" }} decrease icon={<FontAwesomeIcon icon={faIcons.faIndent} flip="horizontal" />} />
                        <RichTextBoxToolbar.IndentButton attrs={{ title: "Increase Indent: ctrl+space\nclear: ctrl+\\" }} icon={<FontAwesomeIcon icon={faIcons.faIndent} />} />
                    </div>
                </RichTextBoxToolbar>
            }
            <Editable
                id={editorKey}
                rows={rows}
                onSelect={() => {
                    editor.onSelect();
                }}
                className={classNames("richtextbox-content editor", editableClassName)}
                readOnly={isReadOnly}
                onBlur={onBlur}
                renderElement={(props) => <InternalElement {...props} {...{ wrapperRef }} />}
                renderLeaf={(props) => <InternalLeaf {...props} {...{ wrapperRef }} />}
                {...{ placeholder, spellCheck }}
                {...remainingEditableProps}
                onKeyDown={editor.onKeyDown}
            />
        </div >
    </Slate>
});
